import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, UploadCloud, Copy, CheckCircle2, ShieldCheck, XCircle, CalendarX2, Ban, Wallet, Camera } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import AirportSearchInput from "@/components/AirportSearchInput";
import RazorpayCheckout from "@/components/RazorpayCheckout";
import SearchableSelect from "@/components/SearchableSelect";
import GamcaCancellationSearch from "@/components/GamcaCancellationSearch";
import GamcaSummaryCard from "@/components/GamcaSummaryCard";
import PhoneCountryCodeInput from "@/components/PhoneCountryCodeInput";
import DatePickerField from "@/components/DatePickerField";
import ReferralCodeField from "@/components/ReferralCodeField";
import { fetchCountries, fetchCitiesForCountry, PROFESSIONS } from "@/lib/countryData";
import { AIRLINES } from "@/lib/airlineData";
import { toLocalDateString } from "@/lib/dateUtils";

const today = () => toLocalDateString();

const SLIP_VERIFY_STEPS = ["Verifying live status on WAFID system"];

export default function ServiceForm({ service, entityType = "service", submitPath = "/applications", onComplete = null, onCurrencyChange = null }) {
  const isAgentContext = submitPath?.startsWith("/agent");
  const navigate = useNavigate();
  const baseRequiresPayment = service.requires_payment !== undefined ? service.requires_payment : Number(service.price_inr || 0) > 0;
  const [formData, setFormData] = useState(() => {
    const initial = {};
    service.form_fields.forEach((f) => {
      if (f.type === "date") initial[f.name] = today();
    });
    return initial;
  });
  const isGamcaCancellation = service.slug === "gamca-cancellation";
  const [cancelSearch, setCancelSearch] = useState(null);
  const requiresPayment = isGamcaCancellation
    ? baseRequiresPayment && !!cancelSearch?.cancellable
    : baseRequiresPayment || (service.slug === "gamca-recovery" && !!formData.gcc_recovery_mode);
  const [countries, setCountries] = useState([]);
  const [citiesMap, setCitiesMap] = useState({});
  const [otherProfession, setOtherProfession] = useState({});
  const [otherAirline, setOtherAirline] = useState({});
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [pendingPayment, setPendingPayment] = useState(null);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    if (isAgentContext) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAgentContext) api.get("/geo/currency").then((res) => setCurrency(res.data.currency)).catch(() => {});
    fetchCountries().then(setCountries).catch(() => {});
  }, []);

  useEffect(() => {
    service.form_fields.filter((f) => f.type === "city").forEach((field) => {
      const countryVal = formData[field.depends_on];
      if (countryVal) {
        fetchCitiesForCountry(countryVal).then((cities) => {
          setCitiesMap((prev) => ({ ...prev, [field.name]: cities }));
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleChange = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleCountryChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field.name]: value };
      const dependentCity = service.form_fields.find((f) => f.depends_on === field.name);
      if (dependentCity) updated[dependentCity.name] = "";
      return updated;
    });
  };

  const [slipStatus, setSlipStatus] = useState(null);
  const [verifyStep, setVerifyStep] = useState(0);
  const slipVerifying = service.slug === "gamca-recovery" && !!uploading["gcc_wafid_slip"];

  useEffect(() => {
    if (!slipVerifying) return;
    setVerifyStep(0);
    const t = setInterval(() => setVerifyStep((s) => Math.min(s + 1, SLIP_VERIFY_STEPS.length - 1)), 4000);
    return () => clearInterval(t);
  }, [slipVerifying]);

  const isGccSlipField = (name) => service.slug === "gamca-recovery" && name === "gcc_wafid_slip";
  const [passportCheck, setPassportCheck] = useState(null);
  const [agentWallet, setAgentWallet] = useState(null);
  const [walletPaying, setWalletPaying] = useState(false);

  useEffect(() => {
    if (isAgentContext) api.get("/agent/wallet").then(({ data }) => {
      setAgentWallet({ balance: data.balance, currency: data.currency || "INR" });
      if (data.currency) setCurrency(data.currency);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgentContext]);

  const payWithWallet = async () => {
    setWalletPaying(true);
    try {
      const { data } = await api.post("/agent/wallet/pay", { application_id: pendingPayment.id });
      toast.success(`Paid instantly from wallet — remaining balance ${agentWallet?.currency === "USD" ? "$" : "₹"}${Number(data.balance).toLocaleString("en-IN")}`);
      if (onComplete) onComplete({ ...pendingPayment, paid: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setWalletPaying(false);
    }
  };

  const handleCancelSearchResult = (r) => {
    setCancelSearch(r);
    if (r?.found) {
      const d = r.details || {};
      setFormData((prev) => ({
        ...prev,
        passport_number: r.passport,
        nationality: r.nationality,
        passport_copy: r.passport_copy || prev.passport_copy || "",
        full_name: d.full_name || "",
        gcc_slip_no: d.slip_no || "",
        medical_center: d.medical_center || "",
        traveling_to: d.traveling_to || "",
        wafid_report_id: d.report_id || "",
        slip_valid_till: d.valid_till || "",
        gcc_cancel_status: r.cancellable ? "cancellable" : (r.wafid_status || "not_cancellable"),
      }));
    }
  };

  const handleFileUpload = async (name, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [name]: true }));
    try {
      const upload = await compressImage(file);
      const fd = new FormData();
      fd.append("file", upload);
      if (isGccSlipField(name)) {
        setSlipStatus(null);
        setFormData((prev) => ({ ...prev, gcc_slip_status: "" }));
        const { data } = await api.post("/gamca/validate-slip", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const owner = data.owner || {};
        setFormData((prev) => ({
          ...prev,
          [name]: data.url,
          gcc_slip_no: data.slip_no || "",
          gcc_slip_barcode: data.barcode_data || "",
          gcc_wafid_report_id: data.report_id || "",
          gcc_wafid_link: data.wafid_link || "",
          gcc_slip_merchant_ref: data.merchant_ref || "",
          gcc_slip_valid_till: data.valid_till || "",
          gcc_slip_status: data.valid ? "verified" : data.recovery_blocked ? "blocked" : "expired",
          gcc_cancelled_via: "",
          gcc_slip_holder: owner.full_name || "",
          gcc_slip_passport: owner.passport_no || "",
          gcc_slip_nationality: owner.nationality || "",
          gcc_slip_traveling_to: owner.traveling_to || "",
          gcc_slip_center: owner.medical_center || "",
        }));
        setSlipStatus({ type: data.valid ? "success" : "warning", message: data.message, owner, slipNo: data.slip_no, validTill: data.valid_till, barcode: data.barcode_data, reportId: data.report_id, wafidLink: data.wafid_link, wafidStatus: data.wafid_status });
        if (data.valid) toast.success("Slip verified successfully");
        else toast.warning(data.message);
      } else if (isGamcaCancellation && name === "passport_copy") {
        setPassportCheck(null);
        fd.append("passport_number", formData.passport_number || "");
        const { data } = await api.post("/gamca/verify-passport", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.matched) {
          handleChange(name, data.url);
          setPassportCheck({ status: "ok", message: data.message });
          toast.success("Passport verified");
        } else {
          handleChange(name, "");
          setPassportCheck({ status: "mismatch", message: data.message });
          toast.error(data.message);
        }
      } else {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        handleChange(name, data.url);
        toast.success(`${file.name} uploaded`);
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      if (isGccSlipField(name)) {
        setFormData((prev) => ({ ...prev, [name]: "", gcc_slip_no: "", gcc_slip_valid_till: "", gcc_slip_status: "" }));
        setSlipStatus({ type: "error", message: msg });
      }
      toast.error(msg);
    } finally {
      setUploading((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleChecklistToggle = (name, option, checked) => {
    setFormData((prev) => {
      const current = Array.isArray(prev[name]) ? prev[name] : [];
      const next = checked ? [...current, option] : current.filter((o) => o !== option);
      return { ...prev, [name]: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isGamcaCancellation && !cancelSearch?.cancellable) {
      toast.error("Please upload your passport first — cancellation must be available to submit");
      return;
    }
    for (const field of service.form_fields) {
      if (field.required && !formData[field.name]) {
        toast.error(`Please fill: ${field.label}`);
        return;
      }
    }
    if (service.slug === "gamca-recovery" && slipStatus?.type === "success" && !formData.gcc_cancelled_via) {
      toast.error("Please select how the slip was cancelled (Cancelled Via)");
      return;
    }
    if (service.slug === "gamca-recovery" && slipStatus?.type === "success" && !formData.gcc_recovery_mode) {
      toast.error("Please select a recovery mode");
      return;
    }
    if (service.slug === "gamca-recovery" && formData.gcc_recovery_mode && !formData.whatsapp_number) {
      toast.error("Please fill: WhatsApp Number");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        form_data: formData,
        customer_name: formData.full_name || formData.name || formData.gcc_slip_holder || "Customer",
        customer_whatsapp: formData.whatsapp_number || formData.mobile_number || "",
        customer_email: formData.email || undefined,
      };
      if (!isAgentContext && localStorage.getItem("gu_ref_agent")) payload.referred_by = localStorage.getItem("gu_ref_agent");
      if (!isAgentContext && referral?.valid && referral.code) payload.referral_code = referral.code;
      if (!isAgentContext && payload.customer_whatsapp) localStorage.setItem("gu_customer_wa", payload.customer_whatsapp);
      if (entityType === "package") payload.package_slug = service.slug;
      else if (entityType === "vacancy") payload.vacancy_slug = service.slug;
      else if (entityType === "visa") payload.visa_slug = service.slug;
      else payload.service_slug = service.slug;
      const { data } = await api.post(submitPath, payload);
      const successState = {
        ...data,
        service_name: service.name || service.title,
        customer_name: formData.full_name || formData.name,
        customer_whatsapp: formData.whatsapp_number || formData.mobile_number,
        currency,
        wafid_pay_link: formData.gcc_recovery_mode === "Self Recovery" ? formData.gcc_wafid_link : undefined,
      };
      if (data.requires_payment) {
        // Wait for payment to succeed before redirecting - keep customer on this page with the Pay Now button
        setPendingPayment(successState);
      } else if (onComplete) {
        onComplete(successState);
      } else {
        navigate("/application-success", { state: successState });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (pendingPayment) {
    const useUsd = pendingPayment.currency === "USD" && pendingPayment.amount_usd > 0;
    const amount = useUsd ? pendingPayment.amount_usd : pendingPayment.amount_inr;
    const payCurrency = useUsd ? "USD" : "INR";
    return (
      <div data-testid="pending-payment-card" className="p-8 rounded-lg border border-[#E5E1DA] bg-white text-center">
        <h3 className="font-heading font-bold text-2xl text-[#1A1A1A] mb-2">Application Submitted!</h3>
        <div className="flex items-center justify-center gap-2 my-4">
          <span data-testid="pending-payment-application-number" className="font-mono text-lg font-semibold text-[var(--brand-primary)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)] px-4 py-2 rounded-md">
            {pendingPayment.application_number}
          </span>
          <Button size="icon" variant="ghost" data-testid="pending-payment-copy-btn" onClick={() => { navigator.clipboard.writeText(pendingPayment.application_number); toast.success("Copied!"); }}>
            <Copy size={16} />
          </Button>
        </div>
        <p className="text-sm text-[var(--brand-secondary)] mb-6">Complete the payment below to confirm your application.</p>
        {isAgentContext && agentWallet !== null && payCurrency === agentWallet.currency && agentWallet.balance >= amount && amount > 0 && (
          <div className="mb-4">
            <Button data-testid="agent-wallet-instant-pay-btn" onClick={payWithWallet} disabled={walletPaying}
              className="w-full sm:w-auto h-12 px-8 bg-[#1A1A1A] hover:bg-[#333] text-white font-bold">
              {walletPaying ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wallet size={16} className="mr-2 text-[#b1976b]" />}
              Pay Instantly from Wallet · Balance {agentWallet.currency === "USD" ? "$" : "₹"}{Number(agentWallet.balance).toLocaleString("en-IN")}
            </Button>
            <p className="text-[11px] text-[#54595f] mt-2">or pay with card / UPI below</p>
          </div>
        )}
        <RazorpayCheckout
          applicationId={pendingPayment.id}
          amount={amount}
          currency={payCurrency}
          customerName={pendingPayment.customer_name}
          customerWhatsapp={pendingPayment.customer_whatsapp}
          onSuccess={() => onComplete ? onComplete({ ...pendingPayment, paid: true }) : navigate("/application-success", { state: { ...pendingPayment, paid: true } })}
        />
      </div>
    );
  }

  return (
    <div>
      <form data-testid="service-application-form" onSubmit={handleSubmit} className="p-6 sm:p-8 rounded-lg border border-[#E5E1DA] bg-white space-y-5">
        <h3 className="font-heading font-bold text-2xl text-[#1A1A1A] mb-2">Apply Now</h3>
        {isGamcaCancellation && <GamcaCancellationSearch onResult={handleCancelSearchResult} />}
        {isGamcaCancellation && cancelSearch?.cancellable && (
          <div data-testid="gcc-cancel-fee-note" className="flex items-center justify-between rounded-lg border border-[color-mix(in_oklab,var(--brand-primary),transparent_75%)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_94%)] px-4 py-3">
            <span className="text-xs font-semibold text-[#1A1A1A]">Cancellation Charge</span>
            <span className="font-price text-lg text-[var(--brand-primary)]">
              {currency === "USD" && Number(service.price_usd) > 0 ? `$${service.price_usd}` : `₹${service.price_inr}`}
            </span>
          </div>
        )}
        {service.form_fields.map((field) => {
          if (isGamcaCancellation && (field.name === "passport_number" || field.name === "passport_copy" || !cancelSearch?.cancellable)) return null;
          let cityOptions = field.type === "city" ? (citiesMap[field.name] || []) : [];
          if (field.type === "city" && field.allowed_cities) {
            const countryVal = formData[field.depends_on];
            const allowed = field.allowed_cities[countryVal];
            if (allowed?.length) cityOptions = cityOptions.filter((c) => allowed.includes(c.value));
          }
          return (
            <div key={field.name}>
              {field.type === "airport" ? (
                <AirportSearchInput label={field.label} value={formData[field.name] || ""} onChange={(v) => handleChange(field.name, v)} testId={`form-field-${field.name}`} />
              ) : field.type === "country" ? (
                <SearchableSelect
                  label={field.label}
                  options={field.allowed_countries?.length ? countries.filter((c) => field.allowed_countries.includes(c.value)) : countries}
                  value={formData[field.name] || ""}
                  onChange={(v) => handleCountryChange(field, v)}
                  placeholder="Search country"
                  testId={`form-field-${field.name}`}
                />
              ) : field.type === "city" ? (
                <SearchableSelect
                  label={field.label}
                  options={cityOptions}
                  value={formData[field.name] || ""}
                  onChange={(v) => handleChange(field.name, v)}
                  disabled={!formData[field.depends_on]}
                  placeholder="Search or type your city"
                  testId={`form-field-${field.name}`}
                  allowManualEntry
                />
              ) : field.type === "profession" ? (
                <div className="space-y-2">
                  <SearchableSelect
                    label={field.label}
                    options={PROFESSIONS}
                    value={otherProfession[field.name] ? "Other" : formData[field.name] || ""}
                    onChange={(v) => {
                      if (v === "Other") {
                        setOtherProfession((p) => ({ ...p, [field.name]: true }));
                        handleChange(field.name, "");
                      } else {
                        setOtherProfession((p) => ({ ...p, [field.name]: false }));
                        handleChange(field.name, v);
                      }
                    }}
                    placeholder="Search profession"
                    testId={`form-field-${field.name}`}
                  />
                  {otherProfession[field.name] && (
                    <Input
                      data-testid={`form-field-${field.name}-other`}
                      placeholder="Please specify your profession"
                      value={formData[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      className="focus-visible:ring-[var(--brand-primary)]"
                    />
                  )}
                </div>
              ) : field.type === "airline" ? (
                <div className="space-y-2">
                  <SearchableSelect
                    label={field.label}
                    options={AIRLINES}
                    value={otherAirline[field.name] ? "Other" : formData[field.name] || ""}
                    onChange={(v) => {
                      if (v === "Other") {
                        setOtherAirline((p) => ({ ...p, [field.name]: true }));
                        handleChange(field.name, "");
                      } else {
                        setOtherAirline((p) => ({ ...p, [field.name]: false }));
                        handleChange(field.name, v);
                      }
                    }}
                    placeholder="Search airline"
                    testId={`form-field-${field.name}`}
                  />
                  {otherAirline[field.name] && (
                    <Input
                      data-testid={`form-field-${field.name}-other`}
                      placeholder="Please specify your airline"
                      value={formData[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      className="focus-visible:ring-[var(--brand-primary)]"
                    />
                  )}
                </div>
              ) : field.type === "phone" ? (
                <div>
                  <PhoneCountryCodeInput
                    label={field.label}
                    value={formData[field.name] || ""}
                    onChange={(v) => {
                      handleChange(field.name, v);
                      if (!isAgentContext) {
                        const dialCode = v.split(" ")[0];
                        if (dialCode) {
                          const c = dialCode === "+91" ? "INR" : "USD";
                          setCurrency(c);
                          onCurrencyChange?.(c);
                        }
                      }
                    }}
                    testId={`form-field-${field.name}`}
                  />
                  {field.name.toLowerCase().includes("whatsapp") && (
                    <p data-testid={`${field.name}-hint`} className="text-xs text-[var(--brand-secondary)] mt-1">
                      Enter your WhatsApp number to get live updates on your application.
                    </p>
                  )}
                </div>
              ) : field.type === "date" ? (
                <DatePickerField
                  label={field.label}
                  value={formData[field.name] || today()}
                  onChange={(v) => handleChange(field.name, v)}
                  testId={`form-field-${field.name}`}
                />
              ) : field.type === "file" ? (
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">{field.label}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label
                      data-testid={`form-field-${field.name}`}
                      className="flex items-center gap-2 border border-dashed border-[color-mix(in_oklab,var(--brand-primary),transparent_50%)] rounded-md px-4 py-3 cursor-pointer hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_95%)] transition-colors"
                    >
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,.webp"
                        className="hidden"
                        onChange={(e) => { handleFileUpload(field.name, e.target.files[0]); e.target.value = ""; }}
                      />
                      {uploading[field.name] ? <Loader2 size={16} className="animate-spin text-[var(--brand-primary)]" /> : <UploadCloud size={16} className="text-[var(--brand-primary)]" />}
                      <span className="text-sm text-[var(--brand-secondary)]">
                        {uploading[field.name] && isGccSlipField(field.name) ? "Verifying slip…" : formData[field.name] ? "File uploaded ✓" : "Click to upload (JPG, PNG, PDF - max 5MB)"}
                      </span>
                    </label>
                    <label
                      data-testid={`form-field-${field.name}-camera`}
                      className="flex items-center gap-2 border border-dashed border-[color-mix(in_oklab,var(--brand-primary),transparent_50%)] rounded-md px-4 py-3 cursor-pointer hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_95%)] transition-colors"
                    >
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => { handleFileUpload(field.name, e.target.files[0]); e.target.value = ""; }}
                      />
                      <Camera size={16} className="text-[var(--brand-primary)]" />
                      <span className="text-sm text-[var(--brand-secondary)]">Take photo with camera</span>
                    </label>
                  </div>
                  {isGamcaCancellation && field.name === "passport_copy" && passportCheck && !uploading[field.name] && (
                    <div data-testid="passport-check-message" className={`mt-2 rounded-md border px-3 py-2 text-[11px] font-medium ${passportCheck.status === "ok" ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-600"}`}>
                      {passportCheck.status === "ok" ? "✅ " : "⚠️ "}{passportCheck.message}
                    </div>
                  )}
                  {isGccSlipField(field.name) && uploading[field.name] && (
                    <div data-testid="gcc-slip-verifying-card" className="mt-2.5 rounded-lg border border-[#E9E4DB] bg-white px-4 py-3.5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2.5">
                        <Loader2 size={15} className="animate-spin text-[var(--brand-primary)]" />
                        <p className="text-sm font-semibold text-[#1A1A1A]">Verifying slip on WAFID system…</p>
                      </div>
                      <div className="space-y-1.5">
                        {SLIP_VERIFY_STEPS.map((step, i) => (
                          <div key={step} data-testid={`gcc-verify-step-${i}`} className="flex items-center gap-2 text-xs">
                            {i < verifyStep ? (
                              <CheckCircle2 size={13} className="text-green-600 shrink-0" />
                            ) : i === verifyStep ? (
                              <Loader2 size={13} className="animate-spin text-[var(--brand-primary)] shrink-0" />
                            ) : (
                              <span className="w-[13px] h-[13px] rounded-full border border-[#D8D2C6] shrink-0" />
                            )}
                            <span className={i < verifyStep ? "text-green-700 font-medium" : i === verifyStep ? "text-[#1A1A1A] font-medium" : "text-[var(--brand-secondary)]/60"}>
                              {step}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {isGccSlipField(field.name) && slipStatus && !uploading[field.name] && (
                    <div className="mt-2.5">
                      {slipStatus.type === "success" ? (
                        <div data-testid="gcc-slip-status-card" className="rounded-lg border border-green-200 bg-green-50/70 px-4 py-3.5">
                          <div className="flex items-start gap-2.5">
                            <ShieldCheck size={18} className="text-green-600 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-green-800">Slip Verified</p>
                              <p data-testid="gcc-slip-status" className="text-xs text-green-700 mt-0.5">{slipStatus.message}</p>
                            </div>
                          </div>
                        </div>
                      ) : slipStatus.type === "warning" ? (
                        <div data-testid="gcc-slip-status-card" className={`rounded-lg border px-4 py-3.5 ${formData.gcc_slip_status === "blocked" ? "border-red-200 bg-red-50/80" : "border-amber-200 bg-amber-50/80"}`}>
                          <div className="flex items-start gap-2.5">
                            {formData.gcc_slip_status === "blocked" ? (
                              <XCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                            ) : (
                              <CalendarX2 size={18} className="text-amber-600 shrink-0 mt-0.5" />
                            )}
                            <div className="min-w-0">
                              <p className={`text-sm font-bold ${formData.gcc_slip_status === "blocked" ? "text-red-700" : "text-amber-800"}`}>
                                {formData.gcc_slip_status === "blocked" ? "Recovery Not Available" : "Slip Expired"}
                              </p>
                              <p data-testid="gcc-slip-status" className={`text-xs mt-0.5 ${formData.gcc_slip_status === "blocked" ? "text-red-600" : "text-amber-700"}`}>
                                {slipStatus.message}
                              </p>
                              {formData.gcc_slip_status === "blocked" && slipStatus.wafidStatus && (
                                <div data-testid="gcc-slip-wafid-status-badge" className="mt-2 inline-flex items-center gap-2.5 rounded-lg border border-red-200 bg-white px-3 py-2 shadow-sm">
                                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--brand-secondary)]">Current Status</span>
                                  <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 text-white ${
                                    slipStatus.wafidStatus === "fit" ? "bg-green-600"
                                    : slipStatus.wafidStatus === "unfit" ? "bg-red-600"
                                    : "bg-amber-500"}`}>
                                    {slipStatus.wafidStatus}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p data-testid="gcc-slip-status" className="text-xs font-medium text-red-600">{slipStatus.message}</p>
                      )}
                      {slipStatus.owner && Object.keys(slipStatus.owner).length > 0 && (
                        <div data-testid="gcc-slip-owner-preview" className="mt-2.5">
                          <GamcaSummaryCard testPrefix="gcc-slip-owner-card" summary={{
                            type: "recovery",
                            full_name: slipStatus.owner.full_name,
                            passport: slipStatus.owner.passport_no,
                            nationality: slipStatus.owner.nationality,
                            slip_no: slipStatus.slipNo,
                            medical_center: slipStatus.owner.medical_center,
                            traveling_to: slipStatus.owner.traveling_to,
                            valid_till: slipStatus.validTill,
                          }} />
                          {slipStatus.type === "success" && (
                            <p className="text-[11px] text-[var(--brand-secondary)]/80 mt-1.5 italic px-1">Please confirm these details match the applicant before continuing.</p>
                          )}
                        </div>
                      )}
                      {slipStatus.type === "success" && (
                        <div data-testid="gcc-cancelled-via-group" className="mt-3">
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--brand-secondary)] mb-1.5">How was this slip cancelled?</p>
                          <div className="grid grid-cols-2 gap-2">
                            <button type="button" data-testid="gcc-cancelled-via-otp"
                              onClick={() => handleChange("gcc_cancelled_via", "OTP")}
                              className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${formData.gcc_cancelled_via === "OTP" ? "border-[var(--brand-primary)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_93%)] ring-1 ring-[var(--brand-primary)]" : "border-[#E5E1DA] bg-white hover:border-[var(--brand-primary)]/50"}`}>
                              <span className="flex items-center justify-between gap-1">
                                <span className="text-xs font-bold text-[#1A1A1A]">OTP</span>
                                {formData.gcc_cancelled_via === "OTP" && <CheckCircle2 size={14} className="text-[var(--brand-primary)] shrink-0" />}
                              </span>
                              <span className="block text-[10px] text-[var(--brand-secondary)] mt-0.5">Cancelled using OTP verification</span>
                            </button>
                            <button type="button" data-testid="gcc-cancelled-via-other"
                              onClick={() => handleChange("gcc_cancelled_via", "Other Method")}
                              className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${formData.gcc_cancelled_via === "Other Method" ? "border-[var(--brand-primary)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_93%)] ring-1 ring-[var(--brand-primary)]" : "border-[#E5E1DA] bg-white hover:border-[var(--brand-primary)]/50"}`}>
                              <span className="flex items-center justify-between gap-1">
                                <span className="text-xs font-bold text-[#1A1A1A]">Other Method</span>
                                {formData.gcc_cancelled_via === "Other Method"
                                  ? <CheckCircle2 size={14} className="text-[var(--brand-primary)] shrink-0" />
                                  : <span className="text-[8px] font-bold uppercase tracking-wide bg-green-600 text-white rounded-full px-1.5 py-0.5 shrink-0">Recommended</span>}
                              </span>
                              <span className="block text-[10px] text-[var(--brand-secondary)] mt-0.5">Cancelled without OTP</span>
                            </button>
                          </div>
                          {formData.gcc_cancelled_via === "OTP" && (
                            <p data-testid="gcc-cancelled-via-warning" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-medium text-red-600">
                              ⚠️ High Risk: The recovered slip may appear on WAFID, but it may not be accepted at the medical center.
                            </p>
                          )}
                          {formData.gcc_cancelled_via === "Other Method" && (
                            <p data-testid="gcc-cancelled-via-success" className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[11px] font-medium text-green-700">
                              ✅ Recommended: The recovered slip will work at the medical center and will be available on the WAFID system.
                            </p>
                          )}
                        </div>
                      )}
                      {slipStatus.type === "success" && formData.gcc_cancelled_via && (
                        <div data-testid="gcc-recovery-mode-group" className="mt-3">
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--brand-secondary)] mb-1.5">Choose Recovery Mode</p>
                          <div className="grid grid-cols-2 gap-2">
                            <button type="button" data-testid="gcc-recovery-mode-self"
                              onClick={() => handleChange("gcc_recovery_mode", "Self Recovery")}
                              className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${formData.gcc_recovery_mode === "Self Recovery" ? "border-[var(--brand-primary)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_93%)] ring-1 ring-[var(--brand-primary)]" : "border-[#E5E1DA] bg-white hover:border-[var(--brand-primary)]/50"}`}>
                              <span className="flex items-center justify-between gap-1">
                                <span className="text-xs font-bold text-[#1A1A1A]">Self Recovery</span>
                                {formData.gcc_recovery_mode === "Self Recovery" && <CheckCircle2 size={14} className="text-[var(--brand-primary)] shrink-0" />}
                              </span>
                              <span className="block text-[10px] text-[var(--brand-secondary)] mt-0.5">Instant — recover it yourself</span>
                              {Number(service.self_price_inr) > 0 && (
                                <span className="block text-sm font-price text-[var(--brand-primary)] mt-1">
                                  {currency === "USD" && Number(service.self_price_usd) > 0 ? `$${service.self_price_usd}` : `₹${service.self_price_inr}`}
                                </span>
                              )}
                            </button>
                            <button type="button" data-testid="gcc-recovery-mode-team"
                              onClick={() => handleChange("gcc_recovery_mode", "Recovery by Our Team")}
                              className={`relative text-left rounded-lg border px-3 py-2.5 transition-colors ${formData.gcc_recovery_mode === "Recovery by Our Team" ? "border-[var(--brand-primary)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_93%)] ring-1 ring-[var(--brand-primary)]" : "border-[#E5E1DA] bg-white hover:border-[var(--brand-primary)]/50"}`}>
                              <span className="flex items-center justify-between gap-1">
                                <span className="text-xs font-bold text-[#1A1A1A]">Recovery by Our Team</span>
                                {formData.gcc_recovery_mode === "Recovery by Our Team"
                                  ? <CheckCircle2 size={14} className="text-[var(--brand-primary)] shrink-0" />
                                  : <span className="text-[8px] font-bold uppercase tracking-wide bg-[var(--brand-primary)] text-white rounded-full px-1.5 py-0.5 shrink-0">Popular</span>}
                              </span>
                              <span className="block text-[10px] text-[var(--brand-secondary)] mt-0.5">Done for you in 5–30 mins</span>
                              {Number(service.team_price_inr) > 0 && (
                                <span className="block text-sm font-price text-[var(--brand-primary)] mt-1">
                                  {currency === "USD" && Number(service.team_price_usd) > 0 ? `$${service.team_price_usd}` : `₹${service.team_price_inr}`}
                                </span>
                              )}
                            </button>
                          </div>
                          {formData.gcc_recovery_mode && (
                            <p data-testid="gcc-recovery-mode-message" className="mt-2 text-[11px] font-medium text-[var(--brand-primary)]">
                              {formData.gcc_recovery_mode === "Self Recovery" ? "Recover it yourself instantly." : "Our team will recover it for you within 5–30 minutes during working hours."}
                            </p>
                          )}
                          {formData.gcc_recovery_mode && (
                            <div className="mt-3">
                              <PhoneCountryCodeInput
                                label="WhatsApp Number"
                                value={formData.whatsapp_number || ""}
                                onChange={(v) => {
                                  handleChange("whatsapp_number", v);
                                  if (!isAgentContext) {
                                    const dialCode = v.split(" ")[0];
                                    if (dialCode) {
                                      const c = dialCode === "+91" ? "INR" : "USD";
                                      setCurrency(c);
                                      onCurrencyChange?.(c);
                                    }
                                  }
                                }}
                                testId="form-field-whatsapp_number"
                              />
                              <p data-testid="whatsapp-number-hint" className="text-[11px] text-[var(--brand-secondary)] mt-1.5">
                                Enter your WhatsApp number to get live updates on your application.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : field.type === "select" ? (
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">{field.label}</Label>
                  <Select value={formData[field.name] || ""} onValueChange={(v) => handleChange(field.name, v)}>
                    <SelectTrigger data-testid={`form-field-${field.name}`} className="focus:ring-[var(--brand-primary)]">
                      <SelectValue placeholder={`Select ${field.label}`} />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {(field.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : field.type === "textarea" ? (
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">{field.label}</Label>
                  <Textarea
                    data-testid={`form-field-${field.name}`}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    className="focus-visible:ring-[var(--brand-primary)]"
                    rows={3}
                  />
                </div>
              ) : field.type === "checklist" ? (
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A] mb-2 block">{field.label}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(field.options || []).map((opt) => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-[var(--brand-secondary)]">
                        <Checkbox
                          data-testid={`form-field-${field.name}-${opt.replace(/\s+/g, "-")}`}
                          checked={(formData[field.name] || []).includes(opt)}
                          onCheckedChange={(checked) => handleChecklistToggle(field.name, opt, checked)}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">{field.label}</Label>
                  <Input
                    data-testid={`form-field-${field.name}`}
                    type={field.type === "number" ? "number" : "text"}
                    placeholder={field.placeholder || ""}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    className="focus-visible:ring-[var(--brand-primary)]"
                  />
                </div>
              )}
            </div>
          );
        })}
        {!isAgentContext && requiresPayment && (
          <ReferralCodeField currency={currency} whatsapp={formData.whatsapp_number || formData.mobile_number || ""} onApplied={setReferral} />
        )}
        {service.slug === "gamca-recovery" && ["expired", "blocked"].includes(formData.gcc_slip_status) ? (
          <div data-testid="slip-expired-block" className="flex items-center justify-center gap-2 rounded-md border border-[#E9E4DB] bg-[#FAF8F4] px-4 py-3.5 text-sm text-[var(--brand-secondary)] font-medium text-center">
            <Ban size={15} className="text-red-500 shrink-0" />
            {formData.gcc_slip_status === "blocked"
              ? "Submission disabled — please upload a different valid GCC slip to continue."
              : "Submission disabled — this slip has expired. Please upload a valid GCC slip to continue."}
          </div>
        ) : isGamcaCancellation && cancelSearch?.found && !cancelSearch?.cancellable ? (
          <div data-testid="cancel-not-possible-block" className="flex items-center justify-center gap-2 rounded-md border border-[#E9E4DB] bg-[#FAF8F4] px-4 py-3.5 text-sm text-[var(--brand-secondary)] font-medium text-center">
            <Ban size={15} className="text-red-500 shrink-0" />
            {cancelSearch?.already_cancelled
              ? "This appointment is already cancelled — no submission needed."
              : "Submission disabled — cancellation is not possible for this appointment."}
          </div>
        ) : slipVerifying ? null : (
          <Button data-testid="service-form-submit-btn" type="submit" disabled={submitting} className="w-full bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white h-12 text-base">
            {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
            {requiresPayment ? "Continue to Payment" : "Submit Application"}
          </Button>
        )}
      </form>

      {service.marketing_highlights?.length > 0 && !submitPath?.startsWith("/agent") && (
        <div data-testid="service-marketing-highlights" className="mt-6 p-5 sm:p-6 rounded-lg border border-[color-mix(in_oklab,var(--brand-primary),transparent_80%)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_95%)]">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--brand-primary)] mb-3">Why Choose Us</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {service.marketing_highlights.map((h, i) => (
              <div key={i} data-testid={`service-marketing-highlight-${i}`} className="flex items-start gap-2 text-sm text-[var(--brand-secondary)]">
                <CheckCircle2 size={16} className="text-[var(--brand-primary)] shrink-0 mt-0.5" />
                <span>{h}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
